import { useCallback, useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useT } from "@/i18n";
import { shortDate } from "@/lib/format";
import { StatCard } from "@/components/StatCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { FileText, Unlock, Gem, Plus, Lock, EyeOff } from "lucide-react";

export default function PostsTab() {
  const t = useT();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", body: "", is_paid: false, price_credits: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try { const { data: d } = await api.get("/coach/analytics/posts"); setData(d); }
    catch { setData({ totals: {}, rows: [] }); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (form.title.trim().length < 2) { toast.error(t("soc.posts_title_required")); return; }
    if (form.is_paid && (parseInt(form.price_credits) || 0) < 1) { toast.error(t("soc.posts_price_required")); return; }
    setSaving(true);
    try {
      await api.post("/feed/post", {
        title: form.title.trim(),
        body: form.body.trim(),
        content_type: "text",
        is_paid: form.is_paid,
        price_credits: form.is_paid ? parseInt(form.price_credits) || 0 : 0,
      });
      toast.success(t("soc.posts_created"));
      setOpen(false);
      setForm({ title: "", body: "", is_paid: false, price_credits: "" });
      await load();
    } catch (err) { toast.error(apiError(err, t("soc.posts_create_failed"))); }
    finally { setSaving(false); }
  };

  const totals = data?.totals || {};
  const rows = data?.rows || [];

  return (
    <div data-testid="soc-posts-tab">
      <div className="mb-4 flex justify-end">
        <Button className="gap-1.5 rounded-full" onClick={() => setOpen(true)} data-testid="content-new">
          <Plus className="h-4 w-4" /> {t("soc.posts_new")}
        </Button>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label={t("soc.posts_stat_posts")} value={totals.posts ?? 0} icon={FileText} testId="content-stat-posts" />
        <StatCard label={t("soc.posts_stat_unlocks")} value={totals.unlocks ?? 0} icon={Unlock} testId="content-stat-unlocks" />
        <StatCard label={t("soc.posts_stat_credits")} value={totals.credits_earned ?? 0} icon={Gem} testId="content-stat-credits" />
        <StatCard label={t("soc.posts_stat_reports")} value={totals.reports ?? 0} icon={EyeOff} testId="content-stat-reports" />
      </div>

      <div className="mt-6">
        {data === null ? (
          <div className="space-y-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-20 rounded-2xl" />)}</div>
        ) : rows.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-border bg-card p-10 text-center">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-accent text-accent-foreground"><FileText className="h-6 w-6" /></div>
            <p className="mt-4 font-display text-lg text-foreground">{t("soc.posts_empty_title")}</p>
            <p className="mt-1 text-sm text-muted-foreground">{t("soc.posts_empty_sub")}</p>
          </div>
        ) : (
          <div className="space-y-2">
            {rows.map((p) => (
              <div key={p.id} data-testid={`content-row-${p.id}`} className="flex items-center gap-3 rounded-xl border border-border bg-card p-3">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="truncate font-semibold text-foreground">{p.title}</p>
                    {p.is_paid && <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-700"><Lock className="h-3 w-3" /> {t("soc.posts_credits", { n: p.price_credits })}</span>}
                    {p.hidden && <span className="rounded-full bg-secondary px-2 py-0.5 text-xs text-muted-foreground">{t("soc.posts_hidden")}</span>}
                  </div>
                  <p className="mt-0.5 text-xs text-muted-foreground">{p.content_type} · {shortDate(p.created_at)}</p>
                </div>
                <div className="shrink-0 text-right text-xs text-muted-foreground">
                  <p>{t("soc.posts_unlocks", { n: p.unlocks })}</p>
                  <p>{t("soc.posts_credits", { n: p.credits_earned })}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="content-dialog">
          <DialogHeader>
            <DialogTitle>{t("soc.posts_dialog_title")}</DialogTitle>
            <DialogDescription>{t("soc.posts_empty_sub")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("soc.posts_f_title")}</label>
              <Input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder={t("soc.posts_f_title_ph")} data-testid="content-title" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("soc.posts_f_body")}</label>
              <Textarea className="min-h-[120px]" value={form.body} onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))} placeholder={t("soc.posts_f_body_ph")} data-testid="content-body" />
            </div>
            <div className="flex items-center justify-between rounded-xl border border-border p-3">
              <div>
                <p className="text-sm font-medium text-foreground">{t("soc.posts_paid")}</p>
                <p className="text-xs text-muted-foreground">{t("soc.posts_paid_sub")}</p>
              </div>
              <Switch checked={form.is_paid} onCheckedChange={(v) => setForm((f) => ({ ...f, is_paid: v }))} data-testid="content-paid" />
            </div>
            {form.is_paid && (
              <div>
                <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("soc.posts_price")}</label>
                <Input type="number" min="1" value={form.price_credits} onChange={(e) => setForm((f) => ({ ...f, price_credits: e.target.value }))} placeholder={t("soc.posts_price_ph")} data-testid="content-price" className="max-w-[160px]" />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button className="rounded-full" onClick={submit} disabled={saving} data-testid="content-submit">
              {saving ? t("soc.posts_publishing") : t("soc.posts_publish")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
